// frontend/src/components/staff/StorePickerRail.jsx
// Staff-only master-detail rail: debounced store search over
// /api/analytics/insights/stores/ plus the selectable store list.
// Shared by Store Activity and Store data health — one rail, one behavior.
//
// Where the page stacks the rail above the details (styles/store-activity.css),
// picking a store folds the rail down to a "Change store" button, so the
// store's details start at the top of the screen instead of under the list.
//
// i18n lives under storeActivity.search / storeActivity.picker; every consumer
// shares that copy.

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, CircularProgress } from '@mui/material';
import SearchField from '../common/SearchField';
import { ICON, Storefront } from '../../icons';
import { sessionApi } from '../../services/SessionApiService';
import { apiErrorFrom } from '../../utils/parseApiError';
import { translateRequestError } from '../../queries/api';
import { useFormatters } from '../../hooks/useFormatters';
import { storeDisplayName, storeDomainIsDistinct } from '../../utils/avatar';
import StoreChip from '../layout/StoreChip';
import '../../styles/store-activity.css';

function StorePickerRail({ selectedId, onSelect }) {
  const { t } = useTranslation();
  const { formatNumber } = useFormatters();

  const [query, setQuery] = useState('');
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  // Re-fetched on search, debounced.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (query.trim()) params.set('q', query.trim());
        const res = await sessionApi.get(`/api/analytics/insights/stores/?${params}`);
        if (!res.ok) throw await apiErrorFrom(res, 'GET /api/analytics/insights/stores/');
        const json = await res.json();
        if (!cancelled) setStores(json);
      } catch (e) {
        console.error('storePickerRail stores load:', e);
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const id = setTimeout(load, 250);
    return () => { cancelled = true; clearTimeout(id); };
  }, [query]);

  return (
    <aside className={`sa-rail${selectedId && !expanded ? ' is-collapsed' : ''}`}>
      <button type="button" className="sa-rail-change" onClick={() => setExpanded(true)}>
        <Storefront size={ICON.md} />
        {t('storeActivity.picker.changeStore')}
      </button>

      <SearchField
        label={t('storeActivity.search.label')}
        placeholder={t('storeActivity.search.placeholder')}
        value={query}
        onChange={setQuery}
      />

      {error && (
        <Alert severity="error">
          {translateRequestError(error, t, 'storeActivity.search.loadFailed')}
        </Alert>
      )}

      <div className="sa-store-list">
        {loading && <div className="sa-rail-empty"><CircularProgress size={18} /></div>}
        {!loading && !error && stores.length === 0 && (
          <div className="sa-rail-empty">{t('storeActivity.search.noResults')}</div>
        )}
        {stores.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => { onSelect(s.id); setExpanded(false); }}
            className={`sa-store${s.id === selectedId ? ' is-active' : ''}`}
          >
            <StoreChip store={s} />
            <span className="sa-store-main">
              <span className="sa-store-name">{storeDisplayName(s)}</span>
              {storeDomainIsDistinct(s) && <span className="sa-store-domain">{s.domain}</span>}
            </span>
            <span className="sa-store-side">
              <span className={`sa-status-dot sa-status-dot--${s.is_active ? 'active' : 'churned'}`} />
              <span className="sa-store-events">{formatNumber(s.event_count, 0)}</span>
            </span>
          </button>
        ))}
      </div>
    </aside>
  );
}

export default StorePickerRail;
